import { createSelector } from '@reduxjs/toolkit'
import {
  activeEntryGuidSelector,
  activeSheetIdSelector,
} from 'apps/formsCreationKit/store/selectors/settings'
import {
  isStandardScreen,
  isTrueStringMatch,
  orderByPosition,
  checkNumberStyleForPaymentTotalField,
  whiteListedTypesForPaymentField,
  paymentDataToIncludeBlacklist,
  setIntersection,
} from 'apps/formsCreationKit/lib/utils'
import { lib } from '@gocanvas/client'
const {
  LOOP_SHEET_TYPE_ID,
  LABELS_ABOVE_SHEET_TYPE_ID,
  LABELS_LEFT_SHEET_TYPE_ID,
  isTable,
 } = lib

export const pastSelector = ({ past }) => past
export const presentSelector = ({ present }) => present
export const futureSelector = ({ future }) => future
export const entities = ({ entities }) => entities

export const entitiesSelector = createSelector(entities, presentSelector)

export const pastEntitiesSelector = createSelector(entities, pastSelector)

export const futureEntitiesSelector = createSelector(entities, futureSelector)

export const formNameSelector = createSelector(
  entitiesSelector,
  ({ app }) => app.name
)

export const entriesSelector = createSelector(
  entitiesSelector,
  ({ entries }) => entries
)

export const sheetsSelector = createSelector(
  entitiesSelector,
  ({ sheets }) => sheets
)

export const sectionsSelector = createSelector(
  entitiesSelector,
  ({ sections }) => sections
)

export const operationsSelector = createSelector(
  entitiesSelector,
  ({ operations }) => operations
)

export const activeEntrySelector = createSelector(
  entriesSelector,
  activeEntryGuidSelector,
  (entries, activeGuid) => entries.find(entry => entry.guid == activeGuid)
)

export const activeSheetSelector = createSelector(
  sheetsSelector,
  activeSheetIdSelector,
  (sheets, activeId) => sheets.find(sheet => sheet.id == activeId)
)

export const appSelector = createSelector(entitiesSelector, ({ app }) => app)

export const appNameSelector = createSelector(appSelector, ({ name }) => name)

export const appIdSelector = createSelector(appSelector, ({ id }) => id)

export const appVersionSelector = createSelector(
  appSelector,
  ({ version }) => version
)

export const multiSectionIds = createSelector(sheetsSelector, sheets =>
  sheets.reduce((acc, { multi_section }) => {
    if (multi_section) {
      acc.push(multi_section)
    }
    return acc
  }, [])
)

export const subSheetsSelector = createSelector(
  sheetsSelector,
  multiSectionIds,
  (sheets, multiSectionIds) =>
    sheets.filter(({ section_id }) => multiSectionIds.includes(section_id))
)

// Finds all simple loop / table sheets (even nested ones) and their children
export const simpleLoopCombinedSheetSelector = createSelector(
  sheetsSelector,
  subSheetsSelector,
  (allSheets, subSheets) => {
    return allSheets.reduce((acc, sheet) => {
      const { sheet_type_id, multi_section } = sheet
      if (sheet_type_id === LOOP_SHEET_TYPE_ID) {
        const updatedSheet = {
          ...sheet,
          subSheets: subSheets.reduce((subAcc, { section_id, id }) => {
            if (multi_section === section_id) {
              subAcc.push(id)
            }
            return subAcc
          }, []),
        }
        acc.push(updatedSheet)
      }
      return acc
    }, [])
  }
)

export const summaryEntriesSelector = createSelector(
  entriesSelector,
  activeEntryGuidSelector,
  (entries, guid) => {
    const whiteListTypeForSummary = ['Sum', 'Number', 'Calculation']
    return entries.filter(
      ({ type, guid: entry_guid }) =>
        entry_guid != guid && whiteListTypeForSummary.includes(type)
    )
  }
)

export const tableCalculationScreenAndEntriesSelector = createSelector(
  sheetsSelector,
  simpleLoopCombinedSheetSelector,
  summaryEntriesSelector,
  (allSheets, simpleLoopSheets, eligibleEntries) => {
    return allSheets.reduce((screens, sheet) => {
      const { id, sheet_type_id } = sheet
      const sheetIds = [`${id}`]
      const canSkip = [LOOP_SHEET_TYPE_ID, LABELS_ABOVE_SHEET_TYPE_ID, LABELS_LEFT_SHEET_TYPE_ID].includes(sheet_type_id)

      const parentSheet = simpleLoopSheets.find(simpleLoop =>
        simpleLoop.subSheets.includes(id)
      )

      if (parentSheet) {
        sheetIds.push(`${parentSheet.id}`)
      }
      else if (canSkip) {
        // these sheets are either not eligible, as they are not in loops, or they are simple loops fields, so will be grouped with their children
        return screens
      }

      const entries = eligibleEntries.filter(({ sheet_id }) =>
        sheetIds.includes(`${sheet_id}`)
      )

      if (!entries.length) {
        return screens
      }

      screens.push({
        sheet: parentSheet || sheet,
        entries,
      })

      return screens
    }, [])
  }
)

// revised unified selector for eligible sheets, given an activeSheet
export const eligibleDependentSheetsSelector = createSelector(
  sheetsSelector,
  multiSectionIds,
  activeSheetIdSelector,
  (sheets, multiSectionIds, activeSheetId) => {
    let eligibleSheets = []
    const topLevelSheets = []
    const primaryLoopSheetsById = new Map()
    const subSheetsBySectionId = new Map()
    const parentSheetsBySectionId = new Map()
    let activeSheetIsSimpleTopLevel = false
    let activeSheet

    // build out lookups
    sheets.forEach(sheet => {
      const { id, multi_section, section_id } = sheet
      activeSheet =
        activeSheet || (isTrueStringMatch(id, activeSheetId) && sheet)

      if (!multiSectionIds.includes(section_id)) {
        // is a top-level sheet (no parent), normal or loop
        topLevelSheets.push(sheet)
        // is a normal screen (no multi_section & not a list)
        if (multi_section) {
          parentSheetsBySectionId.set(multi_section, sheet)
        } else if (!multi_section && isStandardScreen(sheet)) {
          eligibleSheets.push(sheet) // top level normal screens are valid
          if (isTrueStringMatch(id, activeSheetId)) {
            activeSheetIsSimpleTopLevel = true
          }
        }
      }
      // top-level of loop or table
      else if (!multiSectionIds.includes(section_id)) {
        primaryLoopSheetsById.set(id, sheet)
        parentSheetsBySectionId.set(multi_section, sheet)
      } else {
        if (!subSheetsBySectionId.has(section_id)) {
          subSheetsBySectionId.set(section_id, [])
        }
        subSheetsBySectionId.get(section_id).push(sheet)
        if (multi_section) {
          parentSheetsBySectionId.set(multi_section, sheet)
        }
      }
    })

    const { sheet_type_id, multi_section } = activeSheet || {}
    // (the one field on a list sheet can't be dependent or have anything depending on it). A list _screen_ could be conditional on an external field, so do not use this for screen conditions...
    const isListSheet = sheet_type_id === LOOP_SHEET_TYPE_ID && !multi_section
    // if the active sheet is a top-level normal sheet or a list sheet, eligibleSheets is correct already
    // if not, we need to start from the active sheet and work up and across
    if (!activeSheetIsSimpleTopLevel && !isListSheet) {
      let parentSheet = parentSheetsBySectionId.get(activeSheet.section_id)
      const activeSheetScreenHierarchy = []
      if (!parentSheet) {
        // activeSheet is a top-level loop/table sheet, so we just need its child sheets
        const subSheets = subSheetsBySectionId.get(multi_section) || []
        const validSubSheets = subSheets.filter(isStandardScreen)
        activeSheetScreenHierarchy.push(...validSubSheets)
      } else {
        // look up and across the multi_section
        while (parentSheet) {
          const subSheets =
            subSheetsBySectionId.get(parentSheet.multi_section) || []
          const validSubSheets = subSheets.filter(isStandardScreen)
          activeSheetScreenHierarchy.unshift(...validSubSheets) // add this sheet & and siblings
          const nextParentSheet = parentSheetsBySectionId.get(
            parentSheet.section_id
          )
          if (!nextParentSheet || !isTable(nextParentSheet)) {
            // if not a table and not the top sheet, add parent
            activeSheetScreenHierarchy.unshift(parentSheet)
          }
          parentSheet = nextParentSheet
        }
      }
      // active sheet and up hierarchy added first
      eligibleSheets.unshift(...activeSheetScreenHierarchy)
    }

    return eligibleSheets.map(sheet => {
      const { section_id } = sheet
      const parentSheet = parentSheetsBySectionId.get(section_id) || {}
      const isSimpleLoopSheet = Boolean(
        parentSheet && parentSheet.sheet_type_id === LOOP_SHEET_TYPE_ID
      )
      const isTableSubSheet = Boolean(parentSheet && isTable(parentSheet))
      const { description, placeholder } =
        isSimpleLoopSheet && isTableSubSheet ? parentSheet : sheet
      return {
        sheet,
        screenLabel: description || placeholder,
        parentSheet,
        isSubSheet: isSimpleLoopSheet || isTableSubSheet,
        isTableSubSheet,
      }
    })
  }
)

// top-level sheets (those not in a multi_section)
export const primarySheetsSelector = createSelector(
  sheetsSelector,
  multiSectionIds,
  (sheets, multiSectionIds) =>
    sheets.filter(({ section_id }) => !multiSectionIds.includes(section_id))
)

export const combinedSheetSelector = createSelector(
  primarySheetsSelector,
  subSheetsSelector,
  (primarySheets, subSheets) => {
    return primarySheets.map(sheet => {
      return {
        ...sheet,
        subSheets: subSheets.reduce((acc, { section_id, id }) => {
          if (sheet.multi_section === section_id) {
            acc.push(id)
          }
          return acc
        }, []),
      }
    })
  }
)

// Returns only primary (top-level) standard sheets
export const standardPrimarySheetsSelector = createSelector(
  primarySheetsSelector,
  sheets => {
    return sheets.filter(
      sheet => isStandardScreen(sheet) && !sheet.multi_section
    )
  }
)

export const standardSheetEntriesSelector = createSelector(
  entriesSelector,
  standardPrimarySheetsSelector,
  (entries, sheets) => {
    const sheetIds = new Set(sheets.map(({ id }) => id))
    return entries.filter(({ sheet_id }) => sheetIds.has(sheet_id))
  }
)

export const paymentFieldScreenAndEntriesSelector = createSelector(
  combinedSheetSelector,
  standardSheetEntriesSelector,
  activeEntrySelector,
  (sheets, entries, { guid: activeEntryGuid }) => {
    const eligibleEntries = entries.filter(
      entry =>
        entry.guid != activeEntryGuid &&
        whiteListedTypesForPaymentField.includes(entry.type) &&
        checkNumberStyleForPaymentTotalField(entry)
    )

    const eligibleSheetIds = eligibleEntries.reduce((acc, { sheet_id }) => {
      if (!acc.includes(sheet_id)) {
        return [...acc, `${sheet_id}`]
      }
      return acc
    }, [])

    return sheets.reduce((acc, sheet) => {
      if (eligibleSheetIds.includes(`${sheet.id}`)) {
        acc.push({
          sheet,
          entries: eligibleEntries.filter(
            ({ sheet_id }) => `${sheet_id}` === `${sheet.id}`
          ),
        })
      }
      return acc
    }, [])
  }
)

export const tableSheetEntriesSelector = createSelector(
  entriesSelector,
  combinedSheetSelector,
  (entries, combinedSheets) => {
    // TODO: Should consider whether we need to remove advanced loop sheets from here and keep it to just table / simple loop sheets
    // There might be some breaking changes if we do this, so I am leaving it for now, but I think that table/simple loops and advanced
    // loops are two very different things with different processing required and should be treated as such
    const loopSheets = combinedSheets.filter(sheet => sheet?.subSheets?.length)

    return loopSheets.reduce((subSheetEntriesOnly, sheet) => {
      const { id, subSheets } = sheet
      subSheets.forEach(subSheetId => {
        subSheetEntriesOnly.push(
          ...entries.filter(
            entry => entry.sheet_id == subSheetId || entry.sheet_id == id
          )
        )
      })
      return subSheetEntriesOnly
    }, [])
  }
)

export const activeTableSheetKeyFieldSelector = createSelector(
  entriesSelector,
  activeSheetIdSelector,
  (entries, activeSheetId) => entries.find(({ keyField, sheet_id }) => keyField && isTrueStringMatch(sheet_id, activeSheetId))
)

export const activeTableSheetEntriesSelector = createSelector(
  entriesSelector,
  combinedSheetSelector,
  activeSheetIdSelector,
  (entries, combinedSheets, activeSheetId) => {
    const sheet = combinedSheets.find(({ id }) => id == activeSheetId)
    if (!sheet) return []

    const { subSheets = [] } = sheet
    const subSheetEntries = subSheets.flatMap(subSheetId =>
      entries.filter(({ sheet_id }) => sheet_id == subSheetId)
    )

    return [
      ...entries.filter(({ sheet_id }) => sheet_id == activeSheetId),
      ...subSheetEntries,
    ]
  }
)

// does not count advanced loops
export const activeFieldInTableSelector = createSelector(
  activeEntrySelector,
  tableSheetEntriesSelector,
  (activeEntry, tableEntries) => {
    if (!activeEntry) {
      return false
    }

    return (
      tableEntries.filter(({ guid }) => guid == activeEntry.guid).length > 0
    )
  }
)

export const sectionPreviewSelector = createSelector(
  sectionsSelector,
  combinedSheetSelector,
  (sections, sheets) => {
    const getSectionInformation = ({ section_id, ...sheet }) => {
      const { position } = sections.find(({ id }) => id === section_id) || {}
      const updateSheet = {
        ...sheet,
        section_id,
        position,
      }
      return updateSheet
    }

    return sheets.map(getSectionInformation).sort(orderByPosition)
  }
)

export const paymentFieldDataToIncludeTableSelector = createSelector(
  entriesSelector,
  activeEntrySelector,
  activeSheetSelector,
  sectionPreviewSelector,
  subSheetsSelector,
  (entries, { guid, position }, currentSheet, sheets, subSheets) => {
    const currentSheetIndex = sheets.findIndex(
      sheet => sheet.id == currentSheet.id
    )

    const childSheet =
      subSheets.find(s => s.section_id == currentSheet.multi_section) || {}

    const sheetsToSearch = sheets
      .map(({ id }, index) => {
        if (index <= currentSheetIndex) {
          return id
        }
      })
      .filter(a => a)

    const combinedSheetIds = [...sheetsToSearch, childSheet.id]

    return entries.filter(entry => {
      if (
        entry.sheet_id != currentSheet.id &&
        combinedSheetIds.includes(entry.sheet_id) &&
        guid !== entry.guid &&
        !paymentDataToIncludeBlacklist.includes(entry.type) &&
        !entry.keyField
      )
        return entry
      else if (
        entry.sheet_id == currentSheet.id &&
        sheetsToSearch.includes(entry.sheet_id) &&
        guid !== entry.guid &&
        !paymentDataToIncludeBlacklist.includes(entry.type) &&
        position > entry.position &&
        !entry.keyField
      ) {
        return entry
      }
    })
  }
)

export const paymentFieldDataToIncludeSelector = createSelector(
  entriesSelector,
  activeEntrySelector,
  activeSheetSelector,
  sectionPreviewSelector,
  (entries, { guid, position }, currentSheet, sheets) => {
    const currentSheetIndex = sheets.findIndex(
      sheet => sheet.id == currentSheet.id
    )

    const sheetsToSearch = sheets
      .map(({ id, subSheets }, index) => {
        if (index <= currentSheetIndex) {
          return subSheets.length ? subSheets[0] : id
        }
      })
      .filter(a => a)

    return entries.filter(entry => {
      if (
        entry.sheet_id != currentSheet.id &&
        sheetsToSearch.includes(entry.sheet_id) &&
        guid !== entry.guid &&
        !paymentDataToIncludeBlacklist.includes(entry.type) &&
        !entry.keyField
      )
        return entry
      else if (
        entry.sheet_id == currentSheet.id &&
        sheetsToSearch.includes(entry.sheet_id) &&
        guid !== entry.guid &&
        !paymentDataToIncludeBlacklist.includes(entry.type) &&
        position > entry.position &&
        !entry.keyField
      ) {
        return entry
      }
    })
  }
)

export const referenceDataFileListSelector = ({ referenceData }) => referenceData

export const referenceDataEntries = createSelector(entriesSelector, entries =>
  entries.filter(
    ({ reference_data_id, reference_data_column }) =>
      reference_data_id && reference_data_column
  )
)

export const liveDataSourcesSelector = ({ liveDataSources }) => liveDataSources

export const dataFileSourcesSelector = createSelector(
  referenceDataFileListSelector,
  liveDataSourcesSelector,
  (refDataFiles, liveDataSources) => [...refDataFiles, ...liveDataSources.map(source => ({ ...source, type: 'live_data' }))]
)


class SheetMap extends Map {
  constructor(entries = [], config = {}) {
    super(entries)
    this.excludedKeys = new Set(config.excludedKeys || [])
  }

  toPlainObject() {
    return Object.fromEntries(
      Array.from(this).filter(([key]) => !this.excludedKeys.has(key))
    )
  }
}

// returns full map of any sheet to any other reachable sheet,
// updated only on sheet updates
export const reachableSheetsSelector = createSelector(
  sheetsSelector,
  (sheets) => {
    const sheetsBySection = new Map()// section_id -> sheet_ids
    const sheetsById = new Map()// sheet_id -> sheet
    const multiSections = new Set()// section_ids
    const sheetsWithMultiSections = new Map()// sheet_id -> section_id
    const topLevelSheets = new Set()// sheet_ids
    const normalSheets = new Set()// sheet_ids
    const tableSheets = new Map()// sheet_ids
    const tableSubSheets = new Map()// (child) sheet_id -> (parent) sheet_id
    const listSheets = new Set()// sheet_ids
    const reachableSheets = new SheetMap([], {
      excludedKeys: [
        'tableSheets', 'tableSubSheets', 'listSheets', 'sheetsById'
      ],
    })

    // first loop builds lookups
    sheets.forEach(sheet => {
      const { id, section_id, sheet_type_id, multi_section, label, placeholder } = sheet
      sheetsById.set(id, sheet)

      reachableSheets.set(id, [])//set up sheet record
      if (isTable(sheet)) {
        // table -> simple loop + table styles
        tableSheets.set(id, {
          id,
          screenLabel: label || placeholder,
        })
      }
      if (!sheetsBySection.has(section_id)) {
        sheetsBySection.set(section_id, new Set())
      }
      if (!multiSections.has(section_id)) {
        // top-level till proven otherwise
        topLevelSheets.add(id)
      }
      // add the sheet to by section
      sheetsBySection.get(section_id).add(id)

      if (multi_section) {
        multiSections.add(multi_section)
        sheetsWithMultiSections.set(id, multi_section)

        if (sheetsBySection.has(multi_section)) {
          // some sheets already seen are in a multi-section
          // so they can't be top-level
          sheetsBySection.get(multi_section).forEach(sheetId => {
            topLevelSheets.delete(sheetId)
          })
        }
      }
      else if (sheet_type_id === LOOP_SHEET_TYPE_ID) {
        listSheets.add(id)
      }
      else {
        // without a multi_section,
        // if not a list sheet, it's a normal sheet
        normalSheets.add(id)
      }
    })

    // find top-level normal sheets
    const topLevelNormalSheets = setIntersection(topLevelSheets, normalSheets)
    const topLevelNormalSheetsArray = [...topLevelNormalSheets]

    const traverseMultiSections = (
      section_id,
      parentSheetId,
      sheetsBefore,
      sheetsAfter
    ) => {
      const sectionSheets = sheetsBySection.get(section_id) || new Set()
      const normalSectionSheets = setIntersection(sectionSheets, normalSheets)

      const sheetsThisLevel = [
        ...sheetsBefore,
        parentSheetId,
        ...normalSectionSheets
      ]

      // keyfield sheet gets all valid before, itself, & child normal section sheets
      reachableSheets.get(parentSheetId).push(...sheetsThisLevel)

      if (tableSheets.get(parentSheetId)) {
        // table sheets will have only one subsheet
        const subSheetId = sectionSheets.values().next().value
        tableSubSheets.set(subSheetId, parentSheetId)
      }

      sectionSheets.forEach(sheetId => {
        const thisSheet = reachableSheets.get(sheetId)
        const multi_section = sheetsWithMultiSections.get(sheetId)
        if (multi_section) {
          traverseMultiSections(
            multi_section,
            sheetId,
            [...sheetsThisLevel],
            [...sheetsAfter]
          )
        }
        else {
          // a normal sheet gets all valid before & normal section sheets (including itself)
          thisSheet.push(...sheetsThisLevel)
        }
        thisSheet.push(...sheetsAfter)
      })
    }

    // second loop: loop through toplevel sheets and build up information on child sheets
    let topNormalSheetsSeenSoFar = 0
    topLevelSheets.forEach((sheetId) => {
      const thisSheet = reachableSheets.get(sheetId)
      // top-level normal and list sheets can reach themselves & all other normal sheets
      if (normalSheets.has(sheetId) || listSheets.has(sheetId)) {
        topNormalSheetsSeenSoFar++
        thisSheet.push(...topLevelNormalSheetsArray)// includes self
      }
      else if (sheetsWithMultiSections.has(sheetId)) {
        const sheetsBefore = topLevelNormalSheetsArray.slice(0, topNormalSheetsSeenSoFar)
        const sheetsAfter = topLevelNormalSheetsArray.slice(topNormalSheetsSeenSoFar)
        const section_id = sheetsWithMultiSections.get(sheetId)
        traverseMultiSections(
          section_id,
          sheetId,
          sheetsBefore,
          sheetsAfter
        )
        thisSheet.push(...sheetsAfter)
      }
    })
    reachableSheets.set('tableSheets', tableSheets)
    reachableSheets.set('tableSubSheets', tableSubSheets)
    reachableSheets.set('listSheets', listSheets)
    reachableSheets.set('sheetsById', sheetsById)
    return reachableSheets
  }
)

// updates on entry updates
// see utils/createDependentEntriesFilterFn
export const eligibleDependentEntriesSelector = createSelector(
  entriesSelector,
  activeEntryGuidSelector,
  activeSheetIdSelector,
  reachableSheetsSelector,
  (entries, activeEntryGuid, activeSheetId, reachableSheets) => {
    const sheetsById = reachableSheets.get('sheetsById')
    const tableSubSheets = reachableSheets.get('tableSubSheets')

    // build entry lookup, exlcuding current active entry
    const entriesBySheetId = new Map()
    entries.forEach(entry => {
      const { guid, sheet_id } = entry
      // do not include the active entry
      if (guid !== activeEntryGuid) {
        // group table child fields with their parent table
        const whichSheetId = tableSubSheets.get(sheet_id)
          ? tableSubSheets.get(sheet_id)
          : sheet_id
        if (!entriesBySheetId.has(whichSheetId)) {
          entriesBySheetId.set(whichSheetId, [])
        }
        entriesBySheetId.get(whichSheetId).push(entry)
      }
    })

    const eligibleSheets = reachableSheets.get(activeSheetId) || []

    return eligibleSheets.reduce((acc, sheetId) => {
      const sheet = sheetsById.get(sheetId)
      const { description, placeholder } = sheet
      const entries = entriesBySheetId.get(sheetId) || []
      if (!entries.length) {
        return acc
      }
      acc.push({
        ...sheet,
        screenLabel: description || placeholder,
        entries: entries.sort(orderByPosition),
      })
      return acc
    }, [])
  }
)

export const dataSourceContextParamsSelector = createSelector(
  entitiesSelector,
  ({ dataSourceContextParams }) => dataSourceContextParams || []
)
